/**
 * The editable price book for Business Controls (Traci's review, 23 Sep 2026: "we want to be able to edit this at all
 * times, without asking you"). Pure.
 *
 * Where each price lives:
 *   package ladder (1–12 h × Photo / Video / Photo + Video)   system_settings 'package_prices' (json), falling back to
 *                                                              the code ladder in packagePricing.js for any cell not set
 *   late booking window and fee per team member               system_settings 'late_booking_window_days' /
 *                                                              'late_booking_fee_per_member_cents', falling back to
 *                                                              lateBookingFee.js
 *   minimum notice, short-notice switch                       system_settings (already live, read by the booking page)
 *   add-ons incl. the video add-on ladder and hourly rates    the addons catalog (already live; Add-On Pricing edits it)
 *
 * A saved change applies to purchases made after it; bookings keep the price they were sold at. The late rule is live:
 * checkout, the server charge and add-ons read it through lib/booking/bookingRules.js (25 Oct 2026). Until the fold-in
 * (checkout and the homepage reading the price book) ships, saved package prices are recorded here and the site keeps
 * the code ladder; resolveLadder() is what the fold-in will call.
 */
import { BASE_PHOTO_PRICES, VIDEO_ONLY_PRICES, PHOTO_VIDEO_PRICES, MAX_PACKAGE_HOURS } from '../constants/packagePricing.js'
import { LATE_BOOKING_FEE_DAYS, LATE_BOOKING_FEE_PER_MEMBER_CENTS } from '../booking/lateBookingFee.js'

export const PACKAGE_TYPES = [
  { key: 'photo', label: 'Photo Only' },
  { key: 'videoOnly', label: 'Video Only' },
  { key: 'photoVideo', label: 'Photo + Video' }
]
export const SETTING_KEYS = { packages: 'package_prices', lateWindow: 'late_booking_window_days', lateFee: 'late_booking_fee_per_member_cents' }
export const HOURS = Array.from({ length: MAX_PACKAGE_HOURS }, (_, i) => i + 1)

/** The code ladder, whole dollars: { photo: { 1: 550, … }, videoOnly: {…}, photoVideo: {…} }. */
export function codeLadder() {
  const pick = map => Object.fromEntries(HOURS.map(h => [h, map[h] ?? null]))
  return { photo: pick(BASE_PHOTO_PRICES), videoOnly: pick(VIDEO_ONLY_PRICES), photoVideo: pick(PHOTO_VIDEO_PRICES) }
}

const validPrice = v => Number.isFinite(Number(v)) && Number(v) > 0 && Number(v) <= 100000

/** Parse the stored json (string or object), keeping only valid whole-dollar cells for known types and hours. */
export function parseStoredLadder(value) {
  let obj = value
  if (typeof value === 'string') { try { obj = JSON.parse(value) } catch { obj = null } }
  const out = {}
  if (!obj || typeof obj !== 'object') return out
  for (const { key } of PACKAGE_TYPES) {
    for (const h of HOURS) {
      const v = obj?.[key]?.[h] ?? obj?.[key]?.[String(h)]
      if (validPrice(v)) { out[key] = out[key] || {}; out[key][h] = Math.round(Number(v)) }
    }
  }
  return out
}

/** The ladder in force: stored cells over the code ladder. `source[type][h]` says which one each cell came from. */
export function resolveLadder(storedValue) {
  const base = codeLadder()
  const stored = parseStoredLadder(storedValue)
  const prices = {}
  const source = {}
  for (const { key } of PACKAGE_TYPES) {
    prices[key] = {}; source[key] = {}
    for (const h of HOURS) {
      const s = stored?.[key]?.[h]
      prices[key][h] = s ?? base[key][h]
      source[key][h] = s !== undefined ? 'price_book' : 'code'
    }
  }
  return { prices, source }
}

/** The late booking rule in force. */
export function resolveLateRule(windowValue, feeValue) {
  const w = Number(windowValue)
  const f = Number(feeValue)
  return {
    window_days: Number.isInteger(w) && w >= 0 && w <= 120 ? w : LATE_BOOKING_FEE_DAYS,
    fee_per_member_cents: Number.isInteger(f) && f >= 0 && f <= 1000000 ? f : LATE_BOOKING_FEE_PER_MEMBER_CENTS,
    source: (windowValue !== undefined && windowValue !== null) || (feeValue !== undefined && feeValue !== null) ? 'price_book' : 'code'
  }
}

/**
 * Validate an edit of the ladder against the prices in force. Returns { ok, errors, changes: [{ type, hours, from, to }],
 * next } where `next` is the full stored json to write (only cells that differ from the code ladder are kept, so a
 * price set back to the code value stops overriding it).
 */
export function planLadderEdit(current, edits) {
  const errors = []
  const changes = []
  const base = codeLadder()
  const next = {}
  for (const { key, label } of PACKAGE_TYPES) {
    for (const h of HOURS) {
      const raw = edits?.[key]?.[h] ?? edits?.[key]?.[String(h)]
      const was = current.prices[key][h]
      let now = was
      if (raw !== undefined && raw !== null && raw !== '') {
        if (!validPrice(raw) || !Number.isInteger(Number(raw))) { errors.push(`${label} ${h} hr: enter a whole-dollar price above $0`); continue }
        now = Number(raw)
      }
      if (now !== was) changes.push({ type: key, hours: h, from: was, to: now })
      if (now !== base[key][h]) { next[key] = next[key] || {}; next[key][h] = now }
    }
  }
  return { ok: errors.length === 0, errors, changes, next }
}

export function planLateRuleEdit(current, edit) {
  const errors = []
  const next = { ...current }
  if (edit?.window_days !== undefined) {
    const w = Number(edit.window_days)
    if (!Number.isInteger(w) || w < 0 || w > 120) errors.push('Late booking window: 0 to 120 days')
    else next.window_days = w
  }
  if (edit?.fee_per_member_cents !== undefined) {
    const f = Number(edit.fee_per_member_cents)
    if (!Number.isInteger(f) || f < 0 || f > 1000000) errors.push('Late booking fee: $0 to $10,000 per team member')
    else next.fee_per_member_cents = f
  }
  const changes = []
  if (next.window_days !== current.window_days) changes.push({ field: 'window_days', from: current.window_days, to: next.window_days })
  if (next.fee_per_member_cents !== current.fee_per_member_cents) changes.push({ field: 'fee_per_member_cents', from: current.fee_per_member_cents, to: next.fee_per_member_cents })
  return { ok: errors.length === 0, errors, changes, next }
}

/** One line per change, for the audit row and the toast. */
export function describeChanges(ladderChanges = [], lateChanges = []) {
  const label = Object.fromEntries(PACKAGE_TYPES.map(t => [t.key, t.label]))
  return [
    ...ladderChanges.map(c => `${label[c.type]} ${c.hours} hr: $${c.from.toLocaleString('en-US')} → $${c.to.toLocaleString('en-US')}`),
    ...lateChanges.map(c => c.field === 'window_days' ? `Late booking window: ${c.from} → ${c.to} days` : `Late booking fee: $${(c.from / 100).toLocaleString('en-US')} → $${(c.to / 100).toLocaleString('en-US')} per team member`)
  ]
}
